import { useCallback, useEffect, memo, useRef, useState, useMemo } from 'react';
import { Loader2, Info, AlertTriangle, Bell } from 'lucide-react';
import { AlertType } from '../types';
import { useAlerts } from '../hooks/useAlerts';
import { AlertCard } from './AlertCard';
import { useNavigate } from 'react-router-dom';
import { useAlertMetrics } from '@/hooks/useAlertMetrics';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { useAlertsV2 } from '../hooks/useAlertsV2';
import { useTakeAlertMutation } from '@/hooks/useAlertMutations';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAlertSound } from '@/hooks/useAlertSound';
import { EnumAlertStatus } from '@/lib/api/types.gen';

interface AlertListProps {
  type: AlertType;
  onCountChange?: (count: number) => void;
}

const ANALOG_THRESHOLD = 0.5; // Threshold for analog stick movement
const GAMEPAD_POLL_RATE = 100; // How often to check gamepad state in ms

// Helper function to determine if an alert is urgent (Intruder or SuspiciousPerson)
const isUrgentAlert = (status?: EnumAlertStatus) => {
  return status === EnumAlertStatus.Intruder || status === EnumAlertStatus.SuspiciousPerson;
};

export const AlertList = memo(({ type, onCountChange }: AlertListProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useAlertsV2(
    type,
    20,
    onCountChange,
    'v2'
  );
  const [selectedAlertIndex, setSelectedAlertIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const { trackAlertSelection } = useAlertMetrics();
  const [takingAlertId, setTakingAlertId] = useState<string | null>(null);
  const takeFF = useFeatureFlagEnabled('take');

  const alerts = useMemo(() => data?.pages.flatMap((page) => page.alerts) || [], [data?.pages]);

  // Separate alerts into urgent and non-urgent groups
  const { urgentAlerts, otherAlerts } = useMemo(() => {
    const urgent = alerts.filter((alert) => isUrgentAlert(alert.status));
    const other = alerts.filter((alert) => !isUrgentAlert(alert.status));
    return { urgentAlerts: urgent, otherAlerts: other };
  }, [alerts]);

  const shouldPlaySound = urgentAlerts.length > 0;
  const isIntruderAlertPresent = useMemo(
    () => urgentAlerts.some((alert) => alert.status === EnumAlertStatus.Intruder),
    [urgentAlerts]
  );
  console.log(isIntruderAlertPresent, shouldPlaySound);
  useAlertSound(shouldPlaySound, isIntruderAlertPresent);

  const navigateToAlert = useCallback(
    (alertId: string | undefined) => {
      if (alertId) {
        const alert = alerts.find((a) => a.id === alertId);
        trackAlertSelection(alertId);
        navigate(`/alert/${alertId}`);
      }
    },
    [navigate, trackAlertSelection, alerts]
  );

  // Setup the mutation
  const takeAlertMutation = useTakeAlertMutation({
    onSuccessCallback: (data, alertId) => {
      setTakingAlertId(null); // Clear loading state
      if (data.tookAlert) {
        // Navigation now happens here after successful 'take'
        const alert = alerts.find((a) => a.id === alertId); // Re-find alert for navigation
        if (alert) {
          trackAlertSelection(alertId);
          navigateToAlert(alertId);
        } else {
          // Should ideally not happen if cache logic is correct
          console.warn(`Alert ${alertId} not found after successful take mutation.`);
        }
      }
      // If !data.tookAlert, the hook handles toast and cache removal
    },
    onErrorCallback: (_error, _alertId) => {
      // Use the new callback
      setTakingAlertId(null); // Clear loading state on error too
    },
  });

  // Handler to be passed to AlertCard
  const handleTakeAlert = useCallback(
    (alertId: string) => {
      if (takingAlertId) return; // Prevent multiple clicks while one is processing

      if (takeFF) {
        setTakingAlertId(alertId); // Set loading state for this specific alert
        takeAlertMutation.mutate(alertId);
      } else {
        navigate(`/alert/${alertId}`);
      }
    },
    [takeAlertMutation, takingAlertId, takeFF, navigate]
  );

  useEffect(() => {
    setSelectedAlertIndex(0);
  }, [type]);

  const handleInput = useCallback(
    (event?: KeyboardEvent) => {
      // Skip if user is typing in an input field
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      // Get number of columns based on current window width
      let numColumns = 1;
      if (window.innerWidth >= 1536) {
        // 2xl
        numColumns = 3;
      } else if (window.innerWidth >= 768) {
        // md
        numColumns = 2;
      }

      // If this is a keyboard event, get the key
      const key = event?.key;

      // If this is a gamepad input, determine which direction
      const gamepad = navigator.getGamepads?.()?.[0];
      const dpadUp = gamepad?.buttons[12]?.pressed;
      const dpadDown = gamepad?.buttons[13]?.pressed;
      const dpadLeft = gamepad?.buttons[14]?.pressed;
      const dpadRight = gamepad?.buttons[15]?.pressed;
      const analogY = gamepad?.axes[1] || 0;
      const analogX = gamepad?.axes[0] || 0;

      // Determine which direction to move based on input
      const isUp = key === 'ArrowUp' || dpadUp || analogY < -ANALOG_THRESHOLD;
      const isDown = key === 'ArrowDown' || dpadDown || analogY > ANALOG_THRESHOLD;
      const isLeft = key === 'ArrowLeft' || dpadLeft || analogX < -ANALOG_THRESHOLD;
      const isRight = key === 'ArrowRight' || dpadRight || analogX > ANALOG_THRESHOLD;
      const isEnter = key === 'Enter' || gamepad?.buttons[0]?.pressed; // A button

      if (isUp) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          const newIndex = prevIndex - numColumns;
          return newIndex >= 0 ? newIndex : prevIndex;
        });
      } else if (isDown) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          const newIndex = prevIndex + numColumns;
          return newIndex < alerts.length ? newIndex : prevIndex;
        });
      } else if (isLeft) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          return prevIndex > 0 ? prevIndex - 1 : prevIndex;
        });
      } else if (isRight) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          return prevIndex < alerts.length - 1 ? prevIndex + 1 : prevIndex;
        });
      } else if (isEnter) {
        event?.preventDefault?.();
        if (selectedAlertIndex !== null && alerts[selectedAlertIndex]) {
          navigateToAlert(alerts[selectedAlertIndex].id);
        }
      }
    },
    [selectedAlertIndex, alerts, navigateToAlert]
  );

  // Handle keyboard events
  useEffect(() => {
    window.addEventListener('keydown', handleInput);
    return () => {
      window.removeEventListener('keydown', handleInput);
    };
  }, [handleInput]);

  // Poll gamepad state
  useEffect(() => {
    const interval = setInterval(() => {
      handleInput();
    }, GAMEPAD_POLL_RATE);

    return () => {
      clearInterval(interval);
    };
  }, [handleInput]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        // Only load more content when user has scrolled far enough
        rootMargin: '200px 0px',
      }
    );

    const currentLoaderRef = loaderRef.current;

    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (selectedAlertIndex !== null) {
      const selectedElement = document.querySelector(`[data-alert-index="${selectedAlertIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [selectedAlertIndex]);

  // Helper function to get the appropriate style for the section header
  const getSectionHeaderStyle = (isUrgent: boolean) => {
    return isUrgent
      ? 'bg-red-600/20 text-white border border-red-600/30'
      : 'bg-teal-600/20 text-white border border-teal-600/30';
  };

  // Helper function to get the appropriate icon for the section header
  const getSectionHeaderIcon = (isUrgent: boolean) => {
    return isUrgent ? (
      <AlertTriangle size={18} className="text-red-500" />
    ) : (
      <Bell size={18} className="text-teal-500" />
    );
  };

  // Helper function to get tooltip content for each section
  const getSectionTooltipContent = (isUrgent: boolean) => {
    if (isUrgent) {
      return (
        <div className="w-64 p-0">
          <h3 className="border-b p-4 text-lg font-semibold text-white">Urgent Alerts</h3>
          <p className="p-4 text-gray-300">
            These alerts show suspicious activity that needs immediate attention. They include
            potential intruders or suspicious persons detected on the premises.
            <br />
            <br />
            <span className="text-xs">Review and action these alerts immediately.</span>
          </p>
        </div>
      );
    } else {
      return (
        <div className="w-64 p-0">
          <h3 className="border-b p-4 text-lg font-semibold text-white">Other Alerts</h3>
          <p className="p-4 text-gray-300">
            These alerts include all other notification types that may require attention but are
            generally lower priority than urgent alerts.
            <br />
            <br />
            <span className="text-xs">
              Examples: Staff members, animals, environmental triggers, or other non-threatening
              activity.
            </span>
          </p>
        </div>
      );
    }
  };

  // Function to render section header
  const renderSectionHeader = (title: string, isUrgent: boolean) => {
    return (
      <div className="sticky top-0 z-10 bg-[#1b1b1b] px-4 py-3">
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-2 rounded-lg px-3 py-1 ${getSectionHeaderStyle(isUrgent)}`}
          >
            {getSectionHeaderIcon(isUrgent)}
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
          <SimpleTooltip
            content={getSectionTooltipContent(isUrgent)}
            className="cursor-pointer bg-card2"
            side="right"
          >
            <Info size={14} className="h-5 w-5 text-lime" />
          </SimpleTooltip>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-[#B7D257]">
        <Loader2 className="mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative">
      {isFetchingNextPage && !isLoading && (
        <div className="absolute right-4 top-4">
          <Loader2 className="animate-spin text-white" size={24} />
        </div>
      )}

      {/* Urgent Alerts Section */}
      {urgentAlerts.length > 0 && (
        <div className="mb-6">
          {renderSectionHeader('Urgent Alerts', true)}
          <div className="grid grid-cols-3 gap-4 p-4 max-2xl:grid-cols-2 max-md:grid-cols-1">
            {urgentAlerts.map((alert, index) => (
              <AlertCard
                key={alert.id || index}
                alert={alert}
                onTakeAlert={handleTakeAlert}
                isTaking={takingAlertId === alert.id}
                type={type}
                isSelected={selectedAlertIndex === index}
                data-alert-index={index}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other Alerts Section */}
      {otherAlerts.length > 0 && (
        <div>
          {renderSectionHeader('Other Alerts', false)}
          <div className="grid grid-cols-3 gap-4 p-4 max-2xl:grid-cols-2 max-md:grid-cols-1">
            {otherAlerts.map((alert, index) => (
              <AlertCard
                key={alert.id || index}
                alert={alert}
                onTakeAlert={handleTakeAlert}
                isTaking={takingAlertId === alert.id}
                type={type}
                isSelected={selectedAlertIndex === urgentAlerts.length + index}
                data-alert-index={urgentAlerts.length + index}
              />
            ))}
          </div>
        </div>
      )}

      {hasNextPage && (
        <div ref={loaderRef} className="flex justify-center py-4">
          {isFetchingNextPage && <Loader2 className="animate-spin text-[#B7D257]" />}
        </div>
      )}

      {!hasNextPage && alerts.length > 0 && (
        <p className="py-4 text-center text-sm text-gray-500">All&nbsp;alerts loaded</p>
      )}

      {!hasNextPage && alerts.length === 0 && (
        <div className="flex h-[calc(100vh-400px)] flex-col items-center justify-center">
          <img src="/icon/logo.svg" alt="Logo" className="h-16 w-16 opacity-50" />
          <p className="mb-1 text-xl font-semibold text-gray-50">No alerts</p>
          <p>Nice work! You're all caught up</p>
        </div>
      )}
    </div>
  );
});
